import { CostDocument, CostLine } from 'dtos/costs/cost-document.dto';
import { apiClient } from 'services/api-client';
import { supabase } from 'services/auth/supabase.client';

async function hashFiles(files: File[]): Promise<string> {
  const hashes = await Promise.all(files.map(async file => {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map(value => value.toString(16).padStart(2, '0')).join('');
  }));
  // A single file uses its own SHA-256, the same identity the Pellvr Agent sends,
  // so a receipt captured by both paths is recognized as one document.
  if (hashes.length === 1) return hashes[0];
  const combined = new TextEncoder().encode(hashes.join(':'));
  const digest = await crypto.subtle.digest('SHA-256', combined);
  return Array.from(new Uint8Array(digest)).map(value => value.toString(16).padStart(2, '0')).join('');
}

export class CostDocumentService {
  static async uploadReceipt(orgId: string, storeId: string, files: File[], sourceType: 'WEB_UPLOAD' | 'WEB_CAMERA' = 'WEB_UPLOAD', documentType: 'RECEIPT' | 'INVOICE' | 'UNKNOWN' = 'UNKNOWN', documentDate?: string): Promise<CostDocument> {
    const initiated = await apiClient.post<any>(`/orgs/${orgId}/stores/${storeId}/cost-documents/upload-url`, {
      source_type: sourceType,
      document_type: documentType,
      document_date: documentDate || undefined,
      file_hash: await hashFiles(files),
      files: files.map(file => ({ file_name: file.name, file_size_bytes: file.size, content_type: file.type })),
    });
    if (initiated.data.duplicate) return initiated.data.document;
    for (const [index, file] of files.entries()) {
      const target = initiated.data.files[index];
      if (!target) throw new Error('The upload service did not return a target for every file.');
      if (supabase && target.upload_token) {
        const { error } = await supabase.storage.from('cost-documents').uploadToSignedUrl(target.file_key, target.upload_token, file);
        if (error) throw new Error(error.message);
      } else {
        const response = await fetch(target.upload_url, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });
        if (!response.ok) throw new Error('Unable to upload receipt.');
      }
    }
    const completed = await apiClient.post<CostDocument>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${initiated.data.document.id}/upload-complete`);
    return completed.data;
  }

  static async get(orgId: string, storeId: string, documentId: string): Promise<CostDocument> {
    const response = await apiClient.get<CostDocument>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${documentId}`);
    return response.data;
  }

  static async updateDocument(orgId: string, storeId: string, documentId: string, input: Partial<Pick<CostDocument, 'supplier_id' | 'supplier_name' | 'invoice_number' | 'document_date' | 'document_type'>>): Promise<CostDocument> {
    const response = await apiClient.patch<CostDocument>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${documentId}`, input);
    return response.data;
  }

  static async listPending(orgId: string, storeId: string): Promise<CostDocument[]> {
    const response = await apiClient.get<CostDocument[]>(`/orgs/${orgId}/stores/${storeId}/cost-documents?status=PROCESSING,NEEDS_REVIEW,READY,FAILED`);
    return response.data;
  }

  static async updateLine(orgId: string, storeId: string, documentId: string, lineId: string, input: Partial<CostLine> & { confirmed?: boolean }): Promise<CostLine> {
    const response = await apiClient.patch<CostLine>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${documentId}/lines/${lineId}`, input);
    return response.data;
  }

  static async apply(orgId: string, storeId: string, documentId: string): Promise<CostDocument> {
    const response = await apiClient.post<CostDocument>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${documentId}/apply`);
    return response.data;
  }

  static async retry(orgId: string, storeId: string, documentId: string): Promise<CostDocument> {
    const response = await apiClient.post<CostDocument>(`/orgs/${orgId}/stores/${storeId}/cost-documents/${documentId}/retry`);
    return response.data;
  }
}
